import {
  DeviceManagementEnvKey,
  DigitalReceiptEnvKey,
  EsignEnvKey,
  HardwareManagementEnvKey,
  NotificationsEnvKey,
  ProvisioningEnvKey,
  ReportingEnvKey,
  SecurityEnvKey,
  TransactionManagementEnvKey,
  UserManagementEnvKey,
} from '../config.const';
import {
  type EnvFlagBooleanString,
  type StandaloneShellEnvFlagDefaults,
  type StandaloneUserType,
  USER_TYPES,
} from './userTypeSwitch.const';

const ENABLE_ENV_PREFIX = 'NX_PUBLIC_ENABLE_';

export const STANDALONE_EDITABLE_ENABLE_FLAG_KEYS = [
  ...Object.values(ProvisioningEnvKey),
  ...Object.values(DeviceManagementEnvKey),
  ...Object.values(HardwareManagementEnvKey),
  ...Object.values(TransactionManagementEnvKey),
  ...Object.values(DigitalReceiptEnvKey),
  ...Object.values(UserManagementEnvKey),
  ...Object.values(ReportingEnvKey),
  ...Object.values(SecurityEnvKey),
  ...Object.values(EsignEnvKey),
  ...Object.values(NotificationsEnvKey),
].filter((key) => key.startsWith(ENABLE_ENV_PREFIX));

const createAllFlagsDisabledProfile = (): StandaloneShellEnvFlagDefaults =>
  Object.fromEntries(
    STANDALONE_EDITABLE_ENABLE_FLAG_KEYS.map((key) => [key, 'false' satisfies EnvFlagBooleanString]),
  ) as StandaloneShellEnvFlagDefaults;

const buildShellProfile = (enabledKeys: readonly string[]): StandaloneShellEnvFlagDefaults => {
  const profile = createAllFlagsDisabledProfile();

  for (const key of enabledKeys) {
    profile[key] = 'true';
  }

  return profile;
};

// AUTO-GENERATED FILE.
// Run `npm run generate:standalone-env-flag-profiles` from workspace root to refresh it.
// Source values files:
// - Customer: ../../live/kubernetes/icp/dev/primary/m360-solution-portals/m360-customer/values.yaml
// - Tenant: ../../live/kubernetes/icp/dev/primary/m360-solution-portals/m360-tenant/values.yaml
// - Developer: ../../live/kubernetes/icp/dev/primary/shared-solution-portals/gdp/values.yaml
export const STANDALONE_PORTAL_ENV_DEFAULTS_BY_USER_TYPE: Record<StandaloneUserType, StandaloneShellEnvFlagDefaults> = {
  [USER_TYPES.customer]: buildShellProfile([
    ProvisioningEnvKey.EnableSyncConfiguration,
    ProvisioningEnvKey.EnableDeviceDiagnosticsFeature,
    ProvisioningEnvKey.EnableViewOutputFeature,
    ProvisioningEnvKey.EnableGeofencing,
    ProvisioningEnvKey.EnableArchivedDeviceFeature,
    ProvisioningEnvKey.EnableVirtualDeviceSuspendResume,
    ProvisioningEnvKey.EnableMerchantLifecycle,
    DeviceManagementEnvKey.EnableTemplateParametersOptions,
    DeviceManagementEnvKey.EnableSmartfieldCategoryFeature,
    DeviceManagementEnvKey.EnableServicesFeature,
    DeviceManagementEnvKey.EnableDeviceBlock,
    DeviceManagementEnvKey.EnableDeviceLog,
    DeviceManagementEnvKey.EnableAdl,
    DeviceManagementEnvKey.EnableParametersImportExport,
    DeviceManagementEnvKey.EnableSmartfieldValuesComputedConfiguration,
    DeviceManagementEnvKey.EnableStoreSmartFieldFeature,
    DeviceManagementEnvKey.EnableTagSmartFieldFeature,
    DeviceManagementEnvKey.EnableFormBuilderSkipDefaultTemplateValues,
    DeviceManagementEnvKey.EnableKeysetSegregation,
    DeviceManagementEnvKey.EnableProgressiveDeployment,
    DeviceManagementEnvKey.EnableSkipBlockedDeployment,
    DeviceManagementEnvKey.EnableDmMultiMerchant,
    DeviceManagementEnvKey.EnableConfigProfilePut,
    DeviceManagementEnvKey.EnableConfigProfileOrdering,
    DeviceManagementEnvKey.EnableFileAsConfig,
    TransactionManagementEnvKey.EnableTransactionsFeature,
    DigitalReceiptEnvKey.EnableDigitalReceiptFeature,
    DigitalReceiptEnvKey.EnableDigitalReceiptV2,
    UserManagementEnvKey.EnableCustomRoleFeature,
    UserManagementEnvKey.EnableUserDetailsEsignFeature,
    UserManagementEnvKey.EnableUserResetPasswordFeature,
    UserManagementEnvKey.EnableIdpSourceUserFeature,
    UserManagementEnvKey.EnableClientServiceAccountFeature,
    ReportingEnvKey.EnableReportBuilderV2,
    SecurityEnvKey.EnableDocumentsFeature,
    EsignEnvKey.EnableEsignSigningKeys,
    EsignEnvKey.EnableEsignSignatures,
    NotificationsEnvKey.EnableWebhookFeature,
    ProvisioningEnvKey.EnableMsSyncConfiguration,
  ]),
  [USER_TYPES.tenant]: buildShellProfile([
    DeviceManagementEnvKey.EnableDownloadRules,
    DeviceManagementEnvKey.EnableServicesFeature,
    DeviceManagementEnvKey.EnableKeysetSegregation,
    DeviceManagementEnvKey.EnableDmMultiMerchant,
    DeviceManagementEnvKey.EnableConfigProfilePut,
    DeviceManagementEnvKey.EnableConfigProfileOrdering,
    UserManagementEnvKey.EnableCustomRoleFeature,
    UserManagementEnvKey.EnableUserResetPasswordFeature,
    EsignEnvKey.EnableEsignSigningKeys,
    EsignEnvKey.EnableEsignSignatures,
  ]),
  [USER_TYPES.developer]: buildShellProfile([
    DeviceManagementEnvKey.EnableAppLinkDelivery,
    DeviceManagementEnvKey.EnableDmMultiMerchant,
  ]),
};

export const getStandalonePortalEnvDefaults = (userType: StandaloneUserType): StandaloneShellEnvFlagDefaults => ({
  ...STANDALONE_PORTAL_ENV_DEFAULTS_BY_USER_TYPE[userType],
});
